/* eslint-disable @typescript-eslint/no-unused-vars */
import { Feet } from "@/libs/units/length";
import {
  Severity,
  valuesExist,
  type ValidationRuleGenerator
} from "../scenarioValidator";
import {
  DLS_INSTALL_LIMIT,
  DLS_LANDING_LIMIT,
  INCL_LANDING_LIMIT,
  MIN_PSD,
  PUMP_INTAKE_PRESSURE_LOWER_LIMIT
} from "../validatorConstants";
import { useScenarioUnits } from "@/composables/units/useScenarioUnits";
import { computed } from "vue";
import { EquipmentCalculatorSectionKey } from "@/composables/scenarioCalculations/calculationTypes";

export const deviationSurveyChecksNames = [
  "pump_setting_depth",
  "install_dls",
  "landing_dls",
  "landing_dls_caution",
  "landing_inclination",
  "target_flow_warning_alert",
  "pbhp_greater_or_equal_sbhp"
] as const;

export const deviationSurveyChecks: ValidationRuleGenerator = ({
  scenario,
  calcs,
  wellProperties,
  parameters
}) => {
  const { getLargeLengthUnit } = useScenarioUnits(scenario);

  return [
    // PSD Check
    {
      name: "pump_setting_depth",
      message: `Pump Setting Depth < ${MIN_PSD} ft - shaft requires engineering review`,
      severity: Severity.warn,
      isActive: wellProperties.value?.top_tubing_id,
      isInvalid: calcs.equipmentLengths.tubingLengthFt.value < MIN_PSD,
      rulePlacement: EquipmentCalculatorSectionKey.WELL_COMPLETION
    },

    // DLS and inclination Checks
    {
      name: "install_dls",
      message:
        getLargeLengthUnit("Required TDH.convert.Feet").value.label ===
        Feet.label
          ? `Dog Leg Severity > ${DLS_INSTALL_LIMIT} deg/100ft while rigging in hole`
          : `Dog Leg Severity > ${DLS_INSTALL_LIMIT} deg/30m while rigging in hole`,
      severity: Severity.error,
      isActive: Boolean(calcs.entryRowsSurfaceToBoe.value),
      isInvalid: calcs.entryRowsSurfaceToBoe.value?.some(
        ([_md, _incl, _az, _vd, dls]: number[]) => dls > DLS_INSTALL_LIMIT
      ),
      rulePlacement: EquipmentCalculatorSectionKey.WELL_COMPLETION
    },
    {
      name: "landing_dls",
      message:
        getLargeLengthUnit("Required TDH.convert.Feet").value.label ===
        Feet.label
          ? `Dog Leg Severity > ${DLS_LANDING_LIMIT} deg/100ft for landing`
          : `Dog Leg Severity > ${DLS_LANDING_LIMIT} deg/30m for landing`,
      severity: Severity.error,
      isActive:
        Boolean(calcs.entryRowsBotToBoe.value) &&
        valuesExist([calcs.equipmentLengths.tubingLengthFt]),
      isInvalid: calcs.entryRowsBotToBoe.value?.some(
        ([measuredDepth, _incl, _az, _vd, dls]: number[]) =>
          dls > DLS_LANDING_LIMIT
      ),
      rulePlacement: EquipmentCalculatorSectionKey.WELL_COMPLETION,
      disablesRules: ["landing_dls_caution"]
    },
    // Caution DLS and inclination Checks
    {
      name: "landing_dls_caution",
      message:
        getLargeLengthUnit("Required TDH.convert.Feet").value.label ===
        Feet.label
          ? `Caution: Dog Leg Severity > 1 deg/100ft for landing`
          : `Caution: Dog Leg Severity > 1 deg/30m for landing`,
      severity: Severity.warn,
      isActive:
        Boolean(calcs.entryRowsBotToBoe.value) &&
        valuesExist([calcs.equipmentLengths.tubingLengthFt]),
      isInvalid: calcs.entryRowsBotToBoe.value?.some(
        ([, , , , dls]: number[]) => dls >= 1 && dls < DLS_LANDING_LIMIT
      ),
      rulePlacement: EquipmentCalculatorSectionKey.WELL_COMPLETION
    },
    {
      name: "landing_inclination",
      message: `Inclination at PSD more than ${INCL_LANDING_LIMIT} degrees from vertical`,
      severity: Severity.error,
      isActive:
        Boolean(calcs.entryRowsBotToBoe.value) &&
        valuesExist([calcs.equipmentLengths.tubingLengthFt]),
      isInvalid: calcs.entryRowsBotToBoe.value?.some(
        ([_md, inclination]: number[]) => inclination > INCL_LANDING_LIMIT
      ),
      rulePlacement: EquipmentCalculatorSectionKey.WELL_COMPLETION
    },

    // Target flow rate vs Max Flow Rate
    {
      name: "target_flow_warning_alert",
      message: `Target Flow is Greater Than Calculated Max Flow at Intake`,
      severity: Severity.info,
      isActive: valuesExist([
        parameters.value?.target_liquid_rate_std_barrel_day,
        calcs.wellFlowingPressureAtDatumPSIA,
        wellProperties.value?.well_test_static_bottom_hole_pressure_psia
      ]),
      isInvalid:
        parameters.value?.target_liquid_rate_std_barrel_day >=
        calcs.maxFlowRateAtIntakeBBLD.value,
      rulePlacement: EquipmentCalculatorSectionKey.WELL_COMPLETION
    },
    // PBHP >= SBHP
    {
      name: "pbhp_greater_or_equal_sbhp",
      message: `SBHP is less than PBHP`,
      severity: Severity.error,
      isActive:
        valuesExist([
          wellProperties.value?.well_test_static_bottom_hole_pressure_psia,
          wellProperties.value?.well_test_producing_bottom_hole_pressure_psia
        ]) &&
        wellProperties.value?.well_test_static_bottom_hole_pressure_psia !== 0,
      isInvalid:
        wellProperties.value?.well_test_static_bottom_hole_pressure_psia <=
        wellProperties.value?.well_test_producing_bottom_hole_pressure_psia,
      rulePlacement: EquipmentCalculatorSectionKey.WELL_COMPLETION
    }
  ];
};
